import {
  RRULE_REPEAT_FREQ,
  RRULE_BYDAY_BASE,
  rruleByDayValueToLabel,
  RRuleByDayBase,
  RRULE_END_TYPE,
  RRuleByDay,
  RRuleBySetPos,
  RRuleEndType,
  RRuleFrequency,
  RRULE_BYSET_POS,
  rruleBySetPosValueToLabel,
  RRULE_BYDAY,
} from '@go-shared/lib/rrule';
import { capitalize } from '@go-shared/lib/string';
import { MONTH_NAMES } from '@go-shared/lib/time';
import { RESERVABLE_EXPERIENCE_TYPE } from '@go-shared/types/reservable';
import {
  BareReserveSchedule,
  ReserveCommunication,
  ReserveScheduleSlot,
} from '@go-shared/types/reserveSchedule';
import { SimpleUser } from '@go-shared/types/user';
import {
  Form,
  FormikConfig,
  FormikErrors,
  FormikProps,
  FormikProvider,
  useFormik,
  yupToFormErrors,
} from 'formik';
import React, { forwardRef, useImperativeHandle } from 'react';
import { ReservableTimeSlots } from '../BasicForms/ReservableTimeSlots';
import {
  BubbleCheckbox,
  BubbleFormGroup,
  BubbleInput,
  BubbleSelect,
  BubbleCheckboxGroup,
  BubbleHintSelect,
  BubbleTypeahead,
} from '../BubbleUI';
import * as Yup from 'yup';
import { mapBy } from '@go-shared/lib/functional';
import { makeFullName } from '@go-shared/lib/utils';
import { ObjectId } from 'bson';
import { ReservableCommunications } from '../BasicForms/ReservableCommunications';
import { BareVenueTemplate } from '@go-shared/types/venueTemplate';
import {
  createDefaultRuleFragments,
  experienceIsRental,
  reserveTimeSorter,
} from '@go-shared/lib/reserve';
import { ReservableEventTemplateSnapshot } from '@go-shared/types/reservable';

export interface ReservableScheduleFormikFormRules {
  _id: string;
  hidden: boolean;
  ruleFragments: {
    start: string;
    until: string;
    isDynamicSetPos: boolean;
    frequency: RRuleFrequency;
    byMonth: number;
    byMonthDay: number;
    bySetPos: RRuleBySetPos;
    byDay: RRuleByDay;
    endType: RRuleEndType;
    count: number;
    interval: number;
  };
  host?: string;
  slots: ReserveScheduleSlot[];
  communications: ReserveCommunication[];
  rentals?: string[];
}

export interface ReservableScheduleFormikForm {
  _id: string;
  property: string;
  template: string;
  rules: ReservableScheduleFormikFormRules[];
}

export function GENERATE_INITIAL_VALUES(
  propertyId: string,
  eventTemplateId: string,
  evtExperienceType: RESERVABLE_EXPERIENCE_TYPE,
  schedule?: BareReserveSchedule
): ReservableScheduleFormikForm {
  return {
    _id: schedule?._id ?? new ObjectId().toHexString(),
    property: schedule?.property ?? propertyId,
    template: schedule?.eventTemplate ?? eventTemplateId,
    rules:
      schedule?.rules.map(rule => ({
        _id: rule._id,
        hidden: rule.hidden ?? false,
        ruleFragments: rule.ruleFragments ?? createDefaultRuleFragments(),
        host: rule.host ?? undefined,
        slots: (rule.slots ?? [])
          .map(slot => ({
            ...slot,
            capacity: experienceIsRental(evtExperienceType) ? 1 : slot.capacity,
          }))
          .sort(reserveTimeSorter),
        communications: rule.communications ?? [],
        rentals: rule.rentals ?? [],
      })) ?? [],
  };
}

export function buildFormik(
  propertyId: string,
  eventTemplateId: string,
  evtExperienceType: RESERVABLE_EXPERIENCE_TYPE,
  edit?: BareReserveSchedule,
  onSubmit?: FormikConfig<ReservableScheduleFormikForm>['onSubmit']
) {
  const formik = useFormik({
    initialValues: GENERATE_INITIAL_VALUES(propertyId, eventTemplateId, evtExperienceType, edit),
    validate: async values => {
      let errors: FormikErrors<ReservableScheduleFormikForm> = {};

      const basicSchema = Yup.object({
        rules: Yup.array().of(
          Yup.object({
            slots: Yup.array().of(
              Yup.object({
                capacity: Yup.number()
                  .transform((v, orig) => (orig === '' ? undefined : v))
                  .min(1, 'Capacity must be at least 1')
                  .required('Capacity is required'),
                dayspan: Yup.number()
                  .transform((v, orig) => (orig === '' ? undefined : v))
                  .min(1, 'Dayspan must be at least 1')
                  .required('Dayspan is required'),
              })
            ),
          })
        ),
      });

      try {
        await basicSchema.validate(values, { abortEarly: false });
      } catch (err) {
        const t = yupToFormErrors<ReservableScheduleFormikForm>(err);
        errors = { ...errors, ...t };
      }

      return errors;
    },
    onSubmit: onSubmit || (() => {}),
  });

  return formik;
}

export interface ResRecFormikExtState {
  values: ReservableScheduleFormikForm;
  disabled: boolean;
}

export interface ReservableScheduleFormikRef {
  reset: () => void;
  submit: () => void;
}

export interface ReservableScheduleFormikProps {
  type: 'create' | 'edit' | 'view' | 'filter';
  disableHost?: boolean;
  experienceType: RESERVABLE_EXPERIENCE_TYPE;
  formik: FormikProps<ReservableScheduleFormikForm>;
  ruleId: string;
  users: SimpleUser[];
  eventSnapshot: ReservableEventTemplateSnapshot;
  venueTemplates: BareVenueTemplate[];
  onStateUpdate?: (state: ResRecFormikExtState) => void;
}

function ReservableScheduleFormik(
  props: ReservableScheduleFormikProps,
  ref: React.ForwardedRef<ReservableScheduleFormikRef>
) {
  const { formik } = props;
  const { values, setFieldValue, setFieldTouched, handleBlur } = formik;

  const ruleIdx = values.rules.findIndex(r => r._id === props.ruleId);
  const rule = values.rules[ruleIdx];
  const { ruleFragments } = rule;
  const ruleFragmentsKeyPath = `rules[${ruleIdx}].ruleFragments`;

  const readOnly = props.type === 'view';

  const usersDict = mapBy(props.users, u => u._id);
  const ticketsDict = mapBy(props.eventSnapshot.reservables.tickets, t => t._id);
  const ticketRentalOpts = props.eventSnapshot.reservables.tickets.map(t => ({
    key: t._id,
    item: t._id,
  }));

  const rentalsKeyPath = `rules[${ruleIdx}].rentals`;
  const rentalsInRule = (rule.rentals ?? []).filter(id => ticketsDict[id]);

  useImperativeHandle(ref, () => ({
    reset: () => formik.resetForm(),
    submit: () => formik.submitForm(),
  }));

  const dynamicSetPos = (
    <div className="col-span-3 flex items-center space-x-2">
      <BubbleCheckbox
        label="on the"
        className="py-1 whitespace-nowrap"
        appearance="rounded"
        formik={[formik, `${ruleFragmentsKeyPath}.isDynamicSetPos`]}
        checked={!!ruleFragments.isDynamicSetPos}
        readOnly={readOnly}
      />
      <BubbleSelect
        block
        allowUndefined={false}
        options={RRULE_BYSET_POS}
        labelExtractor={rruleBySetPosValueToLabel}
        readOnly={readOnly}
        name={`${ruleFragmentsKeyPath}.bySetPos`}
        value={ruleFragments.bySetPos}
        onValue={val => setFieldValue(`${ruleFragmentsKeyPath}.bySetPos`, val)}
        disabled={!ruleFragments.isDynamicSetPos}
        onBlur={handleBlur}
      />
      <BubbleSelect
        block
        allowUndefined={false}
        options={RRULE_BYDAY}
        labelExtractor={rruleByDayValueToLabel}
        readOnly={readOnly}
        name={`${ruleFragmentsKeyPath}.byDay`}
        value={ruleFragments.byDay}
        onValue={val => setFieldValue(`${ruleFragmentsKeyPath}.byDay`, val)}
        disabled={!ruleFragments.isDynamicSetPos}
        onBlur={handleBlur}
      />
    </div>
  );

  function dynamicEvery(type: 'year(s)' | 'month(s)' | 'week(s)' | 'day(s)') {
    return (
      <div className="col-span-3 flex items-center space-x-2">
        <span className="whitespace-nowrap">every</span>
        <BubbleInput
          block
          type="number"
          name={`${ruleFragmentsKeyPath}.interval`}
          formik={[formik, `${ruleFragmentsKeyPath}.interval`]}
          value={ruleFragments.interval}
          readOnly={readOnly}
          min={0}
        />
        <span className="whitespace-nowrap">{type}</span>
      </div>
    );
  }

  return (
    <FormikProvider value={formik}>
      <Form>
        <div className="grid grid-cols-4 gap-4">
          <BubbleFormGroup label="Repeats" className="col-span-4">
            <div className="inline-flex items-center space-x-2">
              <BubbleSelect
                block
                allowUndefined={false}
                options={RRULE_REPEAT_FREQ}
                labelExtractor={l => (l === 'NEVER' ? 'Single Occurrence' : capitalize(l))}
                name={`${ruleFragmentsKeyPath}.frequency`}
                value={ruleFragments.frequency}
                onValue={val => {
                  setFieldValue(`${ruleFragmentsKeyPath}.frequency`, val);
                  setFieldValue(`${ruleFragmentsKeyPath}.bySetPos`, 1);
                  setFieldValue(`${ruleFragmentsKeyPath}.byDay`, 'MO');
                  setFieldValue(`${ruleFragmentsKeyPath}.isDynamicSetPos`, false);
                }}
                readOnly={readOnly}
                onBlur={handleBlur}
              />
              {ruleFragments.frequency === 'YEARLY'
                ? dynamicEvery('year(s)')
                : ruleFragments.frequency === 'MONTHLY'
                ? dynamicEvery('month(s)')
                : ruleFragments.frequency === 'WEEKLY'
                ? dynamicEvery('week(s)')
                : ruleFragments.frequency === 'DAILY'
                ? dynamicEvery('day(s)')
                : null}
            </div>
          </BubbleFormGroup>

          {!['NEVER', 'DAILY'].includes(ruleFragments.frequency) && (
            <div className="col-span-4 space-y-2">
              {ruleFragments.frequency === 'YEARLY' ? (
                <>
                  <div className="flex items-center space-x-2">
                    <BubbleCheckbox
                      label="on"
                      className="py-1 whitespace-nowrap"
                      appearance="rounded"
                      name={`${ruleFragmentsKeyPath}.isDynamicSetPos`}
                      checked={!ruleFragments.isDynamicSetPos}
                      readOnly={readOnly}
                      onValue={val =>
                        setFieldValue(`${ruleFragmentsKeyPath}.isDynamicSetPos`, !val)
                      }
                      onBlur={handleBlur}
                    />
                    <BubbleSelect
                      block
                      allowUndefined={false}
                      options={Array.from(Array(12).keys())}
                      labelExtractor={val => MONTH_NAMES[val]}
                      name={`${ruleFragmentsKeyPath}.byMonth`}
                      value={ruleFragments.byMonth}
                      onValue={val => setFieldValue(`${ruleFragmentsKeyPath}.byMonth`, val)}
                      readOnly={readOnly}
                      disabled={ruleFragments.isDynamicSetPos}
                      onBlur={handleBlur}
                    />
                    <BubbleSelect
                      block
                      allowUndefined={false}
                      options={Array.from(Array(31).keys()).map(i => i + 1)}
                      name={`${ruleFragmentsKeyPath}.byMonthDay`}
                      value={ruleFragments.byMonthDay}
                      onValue={val => setFieldValue(`${ruleFragmentsKeyPath}.byMonthDay`, val)}
                      readOnly={readOnly}
                      disabled={ruleFragments.isDynamicSetPos}
                      onBlur={handleBlur}
                    />
                  </div>
                  {dynamicSetPos}
                </>
              ) : ruleFragments.frequency === 'MONTHLY' ? (
                <>
                  <div className="flex items-center space-x-2">
                    <BubbleCheckbox
                      label="on day"
                      className="py-1 whitespace-nowrap"
                      appearance="rounded"
                      name={`${ruleFragmentsKeyPath}.isDynamicSetPos`}
                      checked={!ruleFragments.isDynamicSetPos}
                      readOnly={readOnly}
                      onValue={val =>
                        setFieldValue(`${ruleFragmentsKeyPath}.isDynamicSetPos`, !val)
                      }
                      onBlur={handleBlur}
                    />
                    <BubbleSelect
                      block
                      allowUndefined={false}
                      options={Array.from(Array(31).keys()).map(i => i + 1)}
                      name={`${ruleFragmentsKeyPath}.byMonthDay`}
                      value={ruleFragments.byMonthDay}
                      onValue={val => setFieldValue(`${ruleFragmentsKeyPath}.byMonthDay`, val)}
                      readOnly={readOnly}
                      disabled={ruleFragments.isDynamicSetPos}
                      onBlur={handleBlur}
                    />
                  </div>
                  {dynamicSetPos}
                </>
              ) : ruleFragments.frequency === 'WEEKLY' ? (
                <>
                  <div className="flex items-center space-x-2">
                    <BubbleCheckboxGroup
                      options={RRULE_BYDAY_BASE}
                      disabled={readOnly}
                      variant="navy"
                      labelExtractor={rruleByDayValueToLabel}
                      value={ruleFragments.byDay.split(',') as RRuleByDayBase[]}
                      optionAs="button"
                      onValue={val => setFieldValue(`${ruleFragmentsKeyPath}.byDay`, val.join(','))}
                    />
                  </div>
                </>
              ) : null}
            </div>
          )}

          <BubbleFormGroup label="Starts On" className="col-span-2">
            <BubbleInput
              block
              type="date"
              name={`${ruleFragmentsKeyPath}.start`}
              value={ruleFragments.start}
              onValue={val => setFieldValue(`${ruleFragmentsKeyPath}.start`, val)}
              onBlur={handleBlur}
              readOnly={readOnly}
            />
          </BubbleFormGroup>
          <div className="col-span-2">
            {ruleFragments.frequency !== 'NEVER' && (
              <BubbleFormGroup label="Ends On">
                <div className="flex items-center space-x-2">
                  <BubbleSelect
                    block
                    allowUndefined={false}
                    options={RRULE_END_TYPE}
                    labelExtractor={capitalize}
                    name={`${ruleFragmentsKeyPath}.endType`}
                    value={ruleFragments.endType}
                    onValue={val => setFieldValue(`${ruleFragmentsKeyPath}.endType`, val)}
                    readOnly={readOnly}
                    onBlur={handleBlur}
                  />
                  {ruleFragments.endType === 'ON DATE' ? (
                    <BubbleInput
                      block
                      type="date"
                      name={`${ruleFragmentsKeyPath}.until`}
                      value={ruleFragments.until}
                      onValue={val => setFieldValue(`${ruleFragmentsKeyPath}.until`, val)}
                      onBlur={handleBlur}
                      readOnly={readOnly}
                    />
                  ) : ruleFragments.endType === 'AFTER OCCURRENCES' ? (
                    <BubbleInput
                      block
                      type="number"
                      min={0}
                      formik={[formik, `${ruleFragmentsKeyPath}.count`]}
                      value={ruleFragments.count}
                      readOnly={readOnly}
                    />
                  ) : null}
                </div>
              </BubbleFormGroup>
            )}
          </div>
          <BubbleFormGroup label="Host" className="col-span-2">
            <BubbleHintSelect
              block
              allowUndefined
              options={props.users.map(u => u._id)}
              menuClassName="max-h-80 overflow-y-auto"
              labelExtractor={u =>
                makeFullName(usersDict[u]?.firstName, usersDict[u]?.lastName) || 'Unknown User'
              }
              readOnly={readOnly}
              disabled={props.disableHost}
              name={`rules[${ruleIdx}].host`}
              value={rule.host}
              onValue={val => setFieldValue(`rules[${ruleIdx}].host`, val)}
              onBlur={handleBlur}
              subtitle="Optional. All revenue goes to the property if no host is assigned."
            />
          </BubbleFormGroup>

          <div className="col-span-2"></div>

          {experienceIsRental(props.experienceType) && (
            <>
              <BubbleFormGroup label="Rentals">
                <BubbleSelect
                  block
                  allowUndefined={false}
                  value={rentalsInRule.length > 0 ? 'some' : 'all'}
                  options={['all', 'some']}
                  labelExtractor={v => (v === 'all' ? 'All' : 'Select')}
                  readOnly={readOnly}
                  onValue={value => {
                    if (value === 'all') {
                      setFieldValue(rentalsKeyPath, []);
                    } else if (ticketRentalOpts.length > 0) {
                      setFieldValue(rentalsKeyPath, [ticketRentalOpts[0].key]);
                    }
                  }}
                  onBlur={() => setFieldTouched(rentalsKeyPath)}
                />
              </BubbleFormGroup>
              {rentalsInRule.length > 0 && (
                <BubbleFormGroup label="Rental List" className="col-span-3">
                  <BubbleTypeahead
                    block
                    placeholder="Search rentals..."
                    menuClassName="max-h-80 overflow-y-auto"
                    labelExtractor={id => ticketsDict[id]?.name || 'Unknown Ticket'}
                    value={rentalsInRule.map(id => ({
                      key: id,
                      refKey: id,
                      item: id,
                    }))}
                    options={ticketRentalOpts}
                    readOnly={readOnly}
                    onAdd={value => {
                      const next = [...rentalsInRule, value.refKey];
                      setFieldValue(rentalsKeyPath, next);
                      setFieldTouched(rentalsKeyPath);
                    }}
                    onRemove={value => {
                      const next = rentalsInRule.filter(rentalId => rentalId !== value.refKey);
                      setFieldValue(rentalsKeyPath, next);
                      setFieldTouched(rentalsKeyPath);
                    }}
                    onClearAll={() => {
                      setFieldValue(rentalsKeyPath, []);
                      setFieldTouched(rentalsKeyPath);
                    }}
                    onBlur={() => setFieldTouched(rentalsKeyPath)}
                  />
                </BubbleFormGroup>
              )}
            </>
          )}

          <BubbleFormGroup className="col-span-4" label="Time Slots">
            <ReservableTimeSlots
              eventSnapshot={props.eventSnapshot}
              experienceType={props.experienceType}
              formik={formik}
              ruleIdx={ruleIdx}
              readOnly={readOnly}
            />
          </BubbleFormGroup>
          <BubbleFormGroup className="col-span-4" label="Communications">
            <ReservableCommunications
              formik={formik}
              ruleIdx={ruleIdx}
              readOnly={readOnly}
              venueTemplates={props.venueTemplates}
            />
          </BubbleFormGroup>
        </div>
      </Form>
    </FormikProvider>
  );
}

export default forwardRef<ReservableScheduleFormikRef, ReservableScheduleFormikProps>(
  ReservableScheduleFormik
);
